'use client';

import localisePath from '@app/lib/helpers/localise-path';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import type { Language } from '@app/lib/store-context/Context';
import { StaticImage, Video } from 'mmds';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import type {
    FeatureStackSliceDefaultPrimary,
    FeatureStackSliceDefaultPrimaryFeatureGroupItem,
} from '../../../prismicio-types';
import s from './FeatureStack.module.css';

type FeatureItem = FeatureStackSliceDefaultPrimaryFeatureGroupItem;

interface FeatureStackProps {
    primary: FeatureStackSliceDefaultPrimary;
}

const FeatureStack = ({ primary }: FeatureStackProps) => {
    const { layout, image_border, feature_group } = primary;
    const [activeIndex, setActiveIndex] = useState(0);
    const params = useParams();
    const lang = params?.lang as Language;

    if (!feature_group?.length) return null;

    const activeItem: FeatureItem = feature_group[activeIndex];
    const isImageLeft = layout === 'Image Left';
    const borderAttr =
        image_border?.toLowerCase().replace(/\s+/g, '-') ?? 'full';
    const isXLWithCta = borderAttr === 'xl-with-cta';
    const hasAnyCta = feature_group.some(
        (item: FeatureItem) => !!item.cta_label
    );

    const videoDesktopUrl =
        'url' in activeItem.video_desktop
            ? activeItem.video_desktop.url
            : undefined;
    const videoMobileUrl =
        'url' in activeItem.video_mobile
            ? activeItem.video_mobile.url
            : undefined;
    const hasVideo = !!(videoDesktopUrl || videoMobileUrl);

    const ctaContent = activeItem.cta_label ? (
        <a
            href={localisePath(activeItem.cta_link ?? '', lang)}
            className={s.ctaLink}
        >
            <span className={s.ctaLabel}>{activeItem.cta_label}</span>
            {activeItem.cta_icon?.url && (
                <img
                    src={proxyImageUrlIfEnabled(activeItem.cta_icon.url)}
                    alt=""
                    aria-hidden="true"
                    className={s.ctaIcon}
                />
            )}
        </a>
    ) : null;

    return (
        <div
            className={`${s.root} ${isImageLeft ? s.imageLeft : s.imageRight}`}
        >
            {/* Left/right panel: context label + feature list */}
            <div className={s.featureListPanel}>
                {activeItem.context && (
                    <div className={s.context}>{activeItem.context}</div>
                )}
                <ul className={s.featureList} role="list">
                    {feature_group.map((item, i) => (
                        <li key={i} className={s.featureListItem}>
                            <button
                                type="button"
                                className={`${s.featureButton} ${
                                    i === activeIndex
                                        ? s.featureButtonActive
                                        : s.featureButtonInactive
                                }`}
                                onClick={() => setActiveIndex(i)}
                                aria-pressed={i === activeIndex}
                            >
                                <span className={s.featureButtonLabel}>
                                    {item.button_label}
                                </span>
                                {item.button_icon?.url && (
                                    <img
                                        src={proxyImageUrlIfEnabled(
                                            item.button_icon.url
                                        )}
                                        alt=""
                                        aria-hidden="true"
                                        className={s.featureButtonIcon}
                                    />
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Image panel: image with optional border, CTA below image for XL with CTA */}
            <div className={s.imagePanel}>
                <div className={s.imageGroup} data-border={borderAttr}>
                    <div className={s.imageWrapper} data-border={borderAttr}>
                        {hasVideo ? (
                            <Video
                                desktopVideoUrl={videoDesktopUrl}
                                mobileVideoUrl={videoMobileUrl}
                                controls={false}
                                muted={true}
                                loop={true}
                                autoplay={true}
                            />
                        ) : (
                            <StaticImage
                                desktopImage={
                                    activeItem.image_desktop?.url ?? undefined
                                }
                                mobileImage={
                                    activeItem.image_mobile?.url ?? undefined
                                }
                                altDescription={activeItem.button_label ?? ''}
                                fullWidth
                                urlTransformer={proxyImageUrlIfEnabled}
                            />
                        )}
                    </div>
                    {isXLWithCta && (
                        <div className={s.ctaBelowImage}>{ctaContent}</div>
                    )}
                </div>
            </div>

            {/* Feature info: active item label + description — below image on mobile */}
            <div className={s.featureInfo}>
                {activeItem.button_label && (
                    <div className={s.featureLabel}>
                        {activeItem.button_label}
                    </div>
                )}
                {activeItem.feature_content && (
                    <p className={s.featureContent}>
                        {activeItem.feature_content}
                    </p>
                )}
                {!isXLWithCta && hasAnyCta && (
                    <div className={s.ctaWrapper}>
                        {ctaContent ?? (
                            <span
                                className={s.ctaLink}
                                style={{ visibility: 'hidden' }}
                                aria-hidden="true"
                            >
                                <span className={s.ctaLabel}>&nbsp;</span>
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureStack;
